import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ShoppingBag, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast, Toaster } from 'sonner';
import { useApp } from '../context/AppContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompleteSignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useApp();
  
  const token = searchParams.get('token');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    location: 'Australia'
  });

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid or missing invite link');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/invite/verify?token=${token}`);
        const data = await response.json();

        if (response.ok && data.valid) {
          setUserData(data.user);
        } else {
          setError(data.detail || 'This invite link has expired or is invalid');
        }
      } catch (err) {
        setError('Could not verify invite link. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/invite/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password: formData.password,
          location: formData.location
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Account created! Logging you in...');
        
        // Auto-login the user
        const loginResult = await login(userData.email, formData.password);
        if (loginResult.success) {
          setTimeout(() => navigate('/'), 1000);
        } else {
          navigate('/login');
        }
      } else {
        toast.error(data.detail || 'Could not complete signup');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const passwordStrength = () => {
    const { password } = formData;
    if (password.length === 0) return null;
    if (password.length < 6) return { text: 'Weak', color: 'bg-red-500', width: '33%' };
    if (password.length < 10) return { text: 'Medium', color: 'bg-yellow-500', width: '66%' };
    return { text: 'Strong', color: 'bg-minted-500', width: '100%' };
  };

  const strength = passwordStrength();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-minted-600 to-minted-700 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p>Verifying your invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-minted-600 to-minted-700 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link Expired</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/join">
            <Button className="w-full bg-minted-600 hover:bg-minted-700">
              Request New Invite
            </Button>
          </Link>
          <p className="text-sm text-gray-500 mt-4">
            Or <Link to="/login" className="text-minted-600 underline">sign in</Link> if you already have an account
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-minted-600 to-minted-700 flex flex-col">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="flex flex-col items-center justify-center px-6 py-8">
        <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-3 shadow-lg">
          <ShoppingBag className="w-8 h-8 text-minted-600" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-1">Almost There! 🎉</h1>
        <p className="text-minted-100 text-sm">Complete your account setup</p>
      </div>

      {/* Form */}
      <div className="flex-1 bg-white rounded-t-3xl px-6 py-8 shadow-xl">
        <div className="bg-minted-50 rounded-xl p-4 mb-6">
          <p className="text-sm text-minted-700">
            Welcome, <strong>{userData?.name}</strong>! Set your password to start using Minted.
          </p>
          <p className="text-xs text-minted-600 mt-1">{userData?.email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="location">Your Location</Label>
            <Input
              id="location"
              type="text"
              placeholder="e.g., Sydney, Melbourne"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="password">Create Password</Label>
            <div className="relative mt-1">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="At least 6 characters"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {strength && (
              <div className="mt-2">
                <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${strength.color} transition-all duration-300`}
                    style={{ width: strength.width }}
                  />
                </div>
                <p className={`text-xs mt-1 ${strength.color.replace('bg-', 'text-')}`}>
                  Password strength: {strength.text}
                </p>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              placeholder="Re-enter your password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              className="mt-1"
            />
            {formData.confirmPassword && formData.password === formData.confirmPassword && (
              <p className="text-xs text-minted-500 mt-1 flex items-center gap-1">
                <Check className="w-3 h-3" /> Passwords match
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full bg-minted-600 hover:bg-minted-700 h-12 text-base mt-6"
            disabled={submitting}
          >
            {submitting ? 'Creating Account...' : 'Complete Signup'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CompleteSignupPage;
